import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

/**
 * ROYAL FIX HOME — Firebase bootstrap.
 *
 * This is the ONLY file in the project allowed to call `initializeApp`.
 * Every other module (services, hooks, contexts) must import the already
 * -initialized `auth`, `db`, and `storage` instances from here, never
 * re-initialize Firebase on their own.
 *
 * Concrete Firestore collection design (Users, Listings, Conversations,
 * Moderation) is intentionally NOT defined here — per the approved
 * architecture, schema decisions are made in the phase that implements
 * each system (Phase 6 / 9 / 10), with full feature context.
 */

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

function getMissingFirebaseKeys(config) {
  return Object.entries(config)
    .filter(([, value]) => !value)
    .map(([key]) => key);
}

const missingFirebaseKeys = getMissingFirebaseKeys(firebaseConfig);

/**
 * True only when every Firebase env variable is present at build time.
 *
 * Without this guard, `getAuth()` throws `auth/invalid-api-key` at module
 * load when VITE_FIREBASE_* values are missing from the production build,
 * which crashes the whole bundle before React mounts (blank white screen).
 * When config is missing we skip initialization and export `null`
 * instances instead; consumers (AuthContext) handle that case.
 */
export const isFirebaseConfigured = missingFirebaseKeys.length === 0;

if (!isFirebaseConfigured) {
  // eslint-disable-next-line no-console
  console.warn(
    `[Royal Fix Home] Missing Firebase env variables: ${missingFirebaseKeys.join(", ")}. ` +
      "Firebase is disabled. Copy .env.example to .env.development / .env.production and fill in your Firebase project config."
  );
}

// Prevents re-initialization during Vite HMR in development.
export const firebaseApp = isFirebaseConfigured
  ? getApps().length
    ? getApp()
    : initializeApp(firebaseConfig)
  : null;

export const auth = firebaseApp ? getAuth(firebaseApp) : null;
export const db = firebaseApp ? getFirestore(firebaseApp) : null;
export const storage = firebaseApp ? getStorage(firebaseApp) : null;
